import { WaterbodyDetail } from '@omniafishing/core';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';
import React, { ReactNode, useEffect, useState } from 'react';
import { isPending, LoadingState } from '../../../../../constants/loading_state';
import {
  XWeatherConditions,
  xWeatherConditionsCurrent,
  xWeatherConditionsFromTo,
} from '../../../../../lib/xweather';
import { PressureTrend } from '../../../../current_conditions/current_conditions_lib';
import SvgCloud from '../../../../svg/cloud';
import SvgLayerAirTemp from '../../../../svg/layer_air_temp';
import SvgLayerPrecipitation from '../../../../svg/layer_precipitation';
import SvgLayerWaterTemp from '../../../../svg/layer_water_temp';
import SvgLayerWind from '../../../../svg/layer_wind';
import SvgPressure from '../../../../svg/pressure';
import SvgWindDirection from '../../../../svg/wind_direction';
import styles from './tab_overview.less';

interface LakeCurrentConditionsProps {
  waterbody: WaterbodyDetail;
}

export const LakeCurrentConditions = (props: LakeCurrentConditionsProps) => {
  const { waterbody } = props;
  const { water_temperature } = waterbody;

  const [emblaRef] = useEmblaCarousel(
    {
      align: 'start',
      dragFree: true,
    },
    []
  );

  const [weatherLoadingState, setWeatherLoadingState] = useState<LoadingState>(
    LoadingState.NOT_STARTED
  );
  const [weatherCurrentData, setWeatherCurrentData] = useState<XWeatherConditions>();
  const [pressureTrend, setPressureTrend] = useState<PressureTrend>();

  useEffect(() => {
    setWeatherLoadingState(LoadingState.PENDING);
    xWeatherConditionsCurrent({
      lat: waterbody.lat,
      lng: waterbody.lng,
    }).then((res) => {
      setWeatherCurrentData(res.data.response[0].periods[0]);
      setWeatherLoadingState(LoadingState.DONE);
    });

    const now = dayjs();
    const previousHour = now.subtract(61, 'minutes').toISOString();
    xWeatherConditionsFromTo({
      lat: waterbody.lat,
      lng: waterbody.lng,
      from: previousHour,
      to: now.toISOString(),
    })
      .then((res) => {
        const [prev, current] = res.data.response[0].periods;
        if (!prev || !current) {
          return;
        }
        let newPressureTrend: PressureTrend = 'steady';
        if (current.spressureIN < prev.spressureIN) {
          newPressureTrend = 'falling';
        } else if (current.spressureIN > prev.spressureIN) {
          newPressureTrend = 'rising';
        }
        setPressureTrend(newPressureTrend);
      })
      .catch((e) => {
        console.error('Error fetching weather conditions:', e.message);
      });
  }, [waterbody.url_slug]);

  interface Condition {
    icon: ReactNode;
    title: string;
    value: ReactNode;
  }
  const conditions: Condition[] = [];

  if (water_temperature != null) {
    conditions.push({
      icon: <SvgLayerWaterTemp className="w-full h-full" />,
      title: 'Water Temp',
      value: `${water_temperature}°F`,
    });
  }

  if (weatherCurrentData?.tempF != null) {
    conditions.push({
      icon: <SvgLayerAirTemp className="w-full h-full" />,
      title: 'Air temp',
      value: `${Math.round(weatherCurrentData.tempF)}°F`,
    });
  }

  if (weatherCurrentData?.windSpeedMPH != null) {
    conditions.push({
      icon: <SvgLayerWind className="w-full h-full" />,
      title: 'Wind',
      value: (
        <div>
          <span className="text-xl whitespace-nowrap">{`${Math.round(
            weatherCurrentData.windSpeedMPH
          )} mph`}</span>
          <span className="text-sm font-normal leading-none text-[#697076] flex items-center gap-1">
            {weatherCurrentData.windDir != null && weatherCurrentData.windDir}
            {weatherCurrentData.windDirDEG != null && (
              <SvgWindDirection
                style={{
                  transform: `rotate(${weatherCurrentData.windDirDEG}deg)`,
                }}
              />
            )}
          </span>
        </div>
      ),
    });
  }

  if (weatherCurrentData?.pop != null) {
    conditions.push({
      icon: <SvgLayerPrecipitation className="w-full h-full" />,
      title: 'Precipitation',
      value: `${Math.round(weatherCurrentData.pop)}%`,
    });
  }

  if (weatherCurrentData?.sky != null) {
    conditions.push({
      icon: <SvgCloud className="w-full h-full" />,
      title: 'Cloud Cover',
      value: `${Math.round(weatherCurrentData.sky)}%`,
    });
  }

  if (weatherCurrentData?.spressureIN) {
    conditions.push({
      icon: <SvgPressure className="w-full h-full" />,
      title: 'Pressure',
      value: (
        <div className="flex flex-col">
          <span className="text-xl">{weatherCurrentData.spressureIN}</span>
          <span className="text-sm font-normal leading-none text-[#697076] capitalize">
            {pressureTrend}
          </span>
        </div>
      ),
    });
  }

  return (
    <div>
      {isPending(weatherLoadingState) ? (
        <span className={styles.weatherLoading}>Loading current weather</span>
      ) : (
        <div ref={emblaRef} className="overflow-hidden -mx-6 pr-6 select-none w-[calc(100%+36px)]">
          <div className="flex gap-3 pl-6">
            {conditions.map((condition) => {
              const { icon, title, value } = condition;

              return (
                <div
                  className={'font-bold p-3 border border-black rounded-xl flex flex-col'}
                  key={title}
                >
                  <span className="whitespace-nowrap font-normal">{title}</span>
                  <div className="flex gap-1 items-center mt-auto">
                    <div className={'w-[30px] text-[#697076]'}>{icon}</div>
                    <div className="text-xl">{value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
